/**
 * Simple Favorites Redux Slice - Just API calls
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import favoritesService from '../../services/favoritesService';
import { logoutUser } from './authSlice';

// Async thunks for favorites operations

/**
 * Toggle favorite and refresh list
 */
export const toggleFavorite = createAsyncThunk(
  'favorites/toggleFavorite',
  async (productId, { dispatch, rejectWithValue }) => {
    try {
      const result = await favoritesService.toggleFavorite(productId);
      // Refresh the favorites list to update everywhere
      dispatch(loadUserFavorites());
      return { productId, ...result };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Load user's favorites
 */
export const loadUserFavorites = createAsyncThunk(
  'favorites/loadUserFavorites',
  async (options = {}, { rejectWithValue }) => {
    try {
      const result = await favoritesService.getUserFavorites(options);
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Check if product is favorite
 */
export const checkFavoriteStatus = createAsyncThunk(
  'favorites/checkFavoriteStatus',
  async (productId, { rejectWithValue }) => {
    try {
      const favorite = await favoritesService.isFavorite(productId);
      return { productId, isFavorite: !!favorite, favorite };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Load favorites count
 */
export const loadFavoritesCount = createAsyncThunk(
  'favorites/loadFavoritesCount',
  async (_, { rejectWithValue }) => {
    try {
      const count = await favoritesService.getFavoritesCount();
      return count;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// Simple initial state
const initialState = {
  favorites: [],
  favoritesCount: 0,
  isLoading: false,
  error: null
};

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    clearFavorites: (state) => {
      state.favorites = [];
      state.favoritesCount = 0;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },

  extraReducers: (builder) => {
    // Load user favorites
    builder
      .addCase(loadUserFavorites.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadUserFavorites.fulfilled, (state, action) => {
        state.isLoading = false;
        state.favorites = action.payload.favorites;
        state.favoritesCount = action.payload.totalCount;
      })
      .addCase(loadUserFavorites.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Handle logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.favorites = [];
      state.favoritesCount = 0;
      state.error = null;
    });
  }
});

// Export actions
export const { clearFavorites, clearError } = favoritesSlice.actions;

// Simple selectors
export const selectFavorites = (state) => state.favorites.favorites;
export const selectFavoritesCount = (state) => state.favorites.favoritesCount;
export const selectFavoritesLoading = (state) => state.favorites.isLoading;
export const selectFavoritesError = (state) => state.favorites.error;

// Check if a product is favorited
export const selectIsProductFavorite = (productId) => (state) => {
  return state.favorites.favorites.some(fav => fav.productId === productId);
};

export default favoritesSlice.reducer;